import { ChangeDetectionStrategy, Component } from '@angular/core';
import { Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { FilterModel } from 'ag-grid-enterprise';
import saveAs from 'file-saver';
import { File as FileModel } from 'src/app/classes/models';
import { GenericObject } from 'src/app/interfaces/generic-object';
import { GridActionColumnActions } from 'src/app/interfaces/grid-action-column-actions';
import { GridActionColumnMap } from 'src/app/interfaces/grid-action-column-map';
import { ConfirmModalComponent } from 'src/app/modals/confirm-modal/confirm-modal.component';
import { AuthService } from 'src/app/services/auth.service';
import { ContractService } from 'src/app/services/contract.service';
import { ToastService } from 'src/app/services/toast.service';
import { UtilityService } from 'src/app/services/utility.service';

@Component({
  selector: 'app-contracts',
  templateUrl: './contracts.component.html',
  styleUrls: ['./contracts.component.less'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class ContractsComponent {
  constructor(
    private Router: Router,
    private ToastService: ToastService,
    private AuthService: AuthService,
    private ContractService: ContractService,
    private ModalService: NgbModal,
    private UtilityService: UtilityService
  ) {
    this.clonedOriginalSuppliedQueryData = structuredClone(
      this.suppliedQueryData
    );
    this.clonedOriginalTitle = structuredClone(this.title);

    const currentNavigation = Router.currentNavigation();
    const state = currentNavigation?.extras?.state;

    if (state) {
      const { query, FilterModel, title } = state;

      if (FilterModel) {
        this.FilterModel = FilterModel;
      }

      if (query) {
        Object.assign(this.suppliedQueryData, query);
      }

      if (title) {
        this.title = this.title.concat(` - ${title}`);
      }
    }
  }

  ColumnIdToColumnActions: GridActionColumnMap = {
    ['$actionColumn']: ['edit', 'download', 'view', 'delete']
  };

  suppliedQueryData: GenericObject = {
    attributes: [
      'isFinalized',
      'status',
      'VendorId',
      'ClientRegisteredVendorId',
      'ExemptVendorId'
    ]
  };
  clonedOriginalSuppliedQueryData: GenericObject;
  reloadGridData: boolean = false;
  title: string = 'Contracts';
  clonedOriginalTitle: string;
  FilterModel?: FilterModel;

  calculatedColumnActions({
    rowData
  }: {
    rowData: any;
  }): GridActionColumnActions[] | null {
    const gridActionColumnActionArr: GridActionColumnActions[] = [];
    const { isFinalized, Files } = rowData;

    gridActionColumnActionArr.push('edit');
    gridActionColumnActionArr.push('download');

    if (isFinalized) {
      gridActionColumnActionArr.push('view');
    }

    gridActionColumnActionArr.push('delete');

    return gridActionColumnActionArr.length > 0
      ? gridActionColumnActionArr
      : null;
  }

  action({
    action,
    rowData
  }: {
    action: GridActionColumnActions;
    rowData: any;
  }) {
    if (!rowData) {
      throw this.ToastService.addErrorToast(
        'Error',
        'No row data found for Contract.',
        { returnError: true }
      );
    }

    const { id } = rowData;

    if (!id) {
      throw this.ToastService.addErrorToast(
        'Error',
        'No Contract Id found in row data.',
        { returnError: true }
      );
    }

    if (action === 'view') {
      return this.Router.navigate(
        [
          'client',
          this.AuthService.getUserCompanyId(),
          'contract',
          rowData.id,
          'review'
        ],
        { state: { viewing: true } }
      );
    }

    if (action === 'edit') {
      return this.Router.navigate(
        [
          'client',
          this.AuthService.getUserCompanyId(),
          'contract',
          rowData.id,
          'edit'
        ],
        { state: { editing: true, ContractId: id } }
      );
    }

    if (action === 'delete') {
      const modalRef = this.ModalService.open(ConfirmModalComponent, {
        centered: true
      });
      modalRef.componentInstance.displayText =
        'Are you sure you want to delete this contract?';
      return modalRef.closed.subscribe({
        next: (confirm) => {
          if (confirm) {
            this.ContractService.deleteContract(id).subscribe({
              next: (result) => {
                this.ToastService.addSuccessToast(
                  'Success',
                  'Contract has been successfully deleted.'
                );
                this.reloadGridData = true;
              },
              error: (err) => {
                throw this.ToastService.addErrorToast('Error', err.message, {
                  returnError: true
                });
              }
            });
          }
        }
      });
    }

    // Downloading this many files at once is sure to make browsers freak out.
    if (action === 'download') {
      return this.ContractService.getContract(
        this.UtilityService.stringifyObjectsInQueryObject({
          id,
          attributes: ['id'],
          File: {}
        })
      ).subscribe({
        next: (Contract) => {
          const { id: ContractId, Files } = Contract;

          if (!ContractId) {
            throw this.ToastService.addErrorToast(
              'Error',
              'Missing ContractId.',
              { returnError: true }
            );
          }

          if (!Files || Files.length === 0) {
            throw this.ToastService.addErrorToast(
              'Error',
              'No contract files to download.',
              { returnError: true }
            );
          }

          if (Files.length > 15) {
            throw this.ToastService.addErrorToast(
              'Error',
              'Too many files to download all at once.',
              { returnError: true }
            );
          }

          Files.forEach((File: FileModel) => {
            const { id: FileId, filepath } = File;

            if (!FileId) {
              throw this.ToastService.addErrorToast('Error', 'Missing FileId', {
                returnError: true
              });
            }

            if (!filepath) {
              throw this.ToastService.addErrorToast(
                'Error',
                'No file path found.',
                { returnError: true }
              );
            }

            this.ContractService.getContractFileS3PresignedUrl(
              ContractId,
              FileId
            ).subscribe({
              next: ({ presignedUrl, filename }) => {
                saveAs(presignedUrl, filename);
              }
            });
          });
        }
      });
    }

    throw this.ToastService.addErrorToast(
      'Error',
      'Error with grid action column.',
      { returnError: true }
    );
  }

  addContract() {
    this.Router.navigate(
      [
        'client',
        this.AuthService.getUserCompanyId(),
        'contract',
        'addContract'
      ],
      { state: { adding: true } }
    );
  }

  onResetFilters(event: boolean) {
    if (event) {
      this.suppliedQueryData = this.clonedOriginalSuppliedQueryData;
      this.title = this.clonedOriginalTitle;
    }
  }
}
